import { useState, useRef, useEffect } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { useBibliographyEntries } from "@/store/editorSelectors";
import type { CitationMode } from "@/lib/tiptap/extensions/citation";

// Mode → short label + the natbib command it exports to. Order is the
// menu order. Parenthetical is the default for new citations.
const MODE_OPTIONS: { mode: CitationMode; label: string; cmd: string }[] = [
  { mode: "parenthetical", label: "Parenthetical", cmd: "\\citep" },
  { mode: "textual", label: "Textual", cmd: "\\citet" },
  { mode: "author", label: "Author", cmd: "\\citeauthor" },
  { mode: "year", label: "Year", cmd: "\\citeyear" },
];

// How the pill previews each mode inline (just the key dressing — the real
// author/year text is resolved by the .bst at compile time).
function pillText(citeKey: string, mode: CitationMode): string {
  const key = citeKey || "?";
  switch (mode) {
    case "textual": return key;
    case "author": return `${key} (auth)`;
    case "year": return `${key} (yr)`;
    case "bare": return key;
    default: return `(${key})`;
  }
}

/**
 * Inline citation pill — rich Node view for the Citation atom node.
 *
 * Renders as a styled monospace pill "[key]" that:
 *   - Dims slightly when the citation key isn't found in the bibliography
 *     (helps users spot typos before export).
 *   - On hover, pops a small card with the full bibliography entry
 *     (author, title, year) when the key resolves.
 *   - Clicking opens an "edit citation" intent by dispatching a custom
 *     event the surrounding app can listen to (BibliographyPanel uses it
 *     today to jump to the entry row).
 *
 * Uses @tiptap/react NodeViewWrapper — ProseMirror places this inline.
 */
export function CitationNodeView({ node, selected, editor, getPos, updateAttributes }: NodeViewProps) {
  const citeKey = (node.attrs.citeKey as string) ?? "";
  const mode = ((node.attrs.mode as CitationMode) ?? "parenthetical");
  const entries = useBibliographyEntries();
  const entry = entries.find((e) => e.citeKey === citeKey);
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  // Close the mode menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menuOpen]);

  const onMouseEnter = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setHovered(true), 180);
  };
  const onMouseLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setHovered(false);
  };

  // Clicking the pill opens a small menu to switch citation mode (the
  // natbib command it exports to) or jump to the bibliography entry.
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setHovered(false);
    setMenuOpen((v) => !v);
  };

  const pickMode = (next: CitationMode) => {
    updateAttributes({ mode: next });
    setMenuOpen(false);
  };

  const openInBibliography = () => {
    setMenuOpen(false);
    const pos = typeof getPos === "function" ? getPos() : undefined;
    window.dispatchEvent(new CustomEvent("lilia:citation:open", {
      detail: { citeKey, editorPos: pos ?? null },
    }));
    if (pos != null) {
      editor.chain().focus().setTextSelection(pos).run();
    }
  };

  const unresolved = !entry;
  const title = entry
    ? [entry.fields.author, entry.fields.title, entry.fields.year].filter(Boolean).join(" · ")
    : `Citation key "${citeKey}" not found in bibliography`;

  return (
    <NodeViewWrapper
      as="span"
      className="citation-pill-host"
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <span
        data-testid="citation-pill"
        data-type="citation"
        data-cite-key={citeKey}
        data-mode={mode}
        data-unresolved={unresolved ? "true" : undefined}
        className={[
          "inline-citation-pill",
          "inline-flex items-center px-1.5 py-0 text-xs font-mono rounded",
          "mx-[1px] cursor-pointer select-none align-baseline",
          unresolved
            ? "bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100"
            : "bg-primary-subtle text-primary-deep border border-primary-subtle hover:bg-primary-subtle",
          selected ? "ring-2 ring-primary-mild ring-offset-1" : "",
        ].join(" ")}
        title={title}
      >
        {pillText(citeKey, mode)}
      </span>

      {/* Mode menu — pick the natbib command this citation exports to. */}
      {menuOpen && (
        <span
          data-testid="citation-mode-menu"
          className="absolute z-50 top-full left-0 mt-1 min-w-[200px] p-1 rounded-md shadow-lg border border-slate-200 bg-white text-slate-700"
          onClick={(e) => e.stopPropagation()}
        >
          {MODE_OPTIONS.map((opt) => (
            <button
              key={opt.mode}
              type="button"
              data-testid="citation-mode-option"
              data-mode={opt.mode}
              data-active={mode === opt.mode ? "true" : "false"}
              onClick={() => pickMode(opt.mode)}
              className={[
                "flex w-full items-center justify-between gap-3 px-2 py-1 text-xs rounded text-left",
                mode === opt.mode ? "bg-primary-subtle text-primary-deep" : "hover:bg-slate-100",
              ].join(" ")}
            >
              <span>{opt.label}</span>
              <span className="font-mono text-[10px] text-slate-400">{opt.cmd}</span>
            </button>
          ))}
          <span className="block my-1 border-t border-slate-100" />
          <button
            type="button"
            data-testid="citation-open-bib"
            onClick={openInBibliography}
            className="flex w-full items-center px-2 py-1 text-xs rounded text-left hover:bg-slate-100"
          >
            Open in bibliography ↗
          </button>
        </span>
      )}

      {hovered && !menuOpen && entry && (
        <span
          className="absolute z-50 bottom-full left-0 mb-1 min-w-[220px] max-w-[340px] p-2 text-xs rounded-md shadow-lg border border-slate-200 bg-white text-slate-700 whitespace-normal"
          style={{ pointerEvents: "none" }}
        >
          <span className="block font-medium truncate">
            {entry.fields.title || citeKey}
          </span>
          {entry.fields.author && (
            <span className="block text-slate-500 truncate">{entry.fields.author}</span>
          )}
          {(entry.fields.year || entry.entryType) && (
            <span className="block text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">
              {[entry.entryType, entry.fields.year].filter(Boolean).join(" · ")}
            </span>
          )}
        </span>
      )}
      {hovered && !menuOpen && unresolved && (
        <span
          className="absolute z-50 bottom-full left-0 mb-1 min-w-[220px] p-2 text-xs rounded-md shadow-lg border border-rose-200 bg-rose-50 text-rose-700"
          style={{ pointerEvents: "none" }}
        >
          <span className="block font-medium">Not in bibliography</span>
          <span className="block text-[10px] mt-0.5 text-rose-500">Click to open citation manager.</span>
        </span>
      )}
    </NodeViewWrapper>
  );
}
